import { BusinessRounded } from '@mui/icons-material';
import { Box, styled, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useCallback, useMemo } from 'react';

import { useTestId } from '../../../../hooks/useTestId';
import { userEditSave } from '../../../../services/dealGPT';
import { CustomTooltip } from '../../../../theme/CustomComponents';
import {
  BenchMarkOption,
  ProjectRunAnalysisQuestion,
  ProjectSalesforceInfo,
} from '../../../../Types/dealGPT';
import { AnalysisQuestionStatus } from '../../../../Types/enums';
import { formatBenchmarkOutput } from '../../../../util/benchmarkUtils';
import { AnalyticsEvent, useAnalytics } from '../../../Providers/AnalyticsProvider';

const StyledToggleButton = styled(ToggleButton)({
  gap: '8px',
  width: '130px',
  height: '50px',
  border: '1px solid #0047FF',
  color: '#0047FF',
  '&.Mui-selected': {
    backgroundColor: '#0047FF',
    color: '#fff',
    borderColor: '#0047FF',
    '&:hover': {
      backgroundColor: '#0037cc',
    },
  },
  '&:hover': {
    backgroundColor: 'rgba(0, 71, 255, 0.08)',
  },
});

interface BenchmarkProps {
  benchmarkQuestion: ProjectRunAnalysisQuestion;
  salesforce: ProjectSalesforceInfo | null;
  refetchQuestions: () => void;
}

const Benchmark: React.FC<BenchmarkProps> = ({
  benchmarkQuestion,
  salesforce,
  refetchQuestions,
}) => {
  const { benchmark } = benchmarkQuestion;
  const analytics = useAnalytics();
  const tid = useTestId('benchmark');

  const benchmarkOptions = useMemo(() => {
    if (!benchmark?.options) return [];

    const availability: Record<BenchMarkOption['type'], boolean> = {
      overall: true,
      industry: !!salesforce?.industry,
      subindustry: !!salesforce?.subIndustry,
      revenue: !!salesforce?.revenueBand,
    };

    // Only display the options that are available based on the salesforce data
    const filteredOptions = benchmark.options.filter((option) =>
      option.type in availability ? availability[option.type] : true
    );

    // Calculate percentages and tooltips for each option
    return filteredOptions.map((option) => {
      // Calculate the percentage of companies that match the benchmark
      const percentage = option.total ? Math.round((option.value * 100) / option.total) : 0;

      const tooltip = formatBenchmarkOutput(benchmark, option, salesforce);

      const disabled = !benchmark || option.total === 0 || option.value === 0;
      return { ...option, disabled, tooltip, percentage };
    });
  }, [benchmark, salesforce]);

  const handleSelectionChange = useCallback(
    async (_: React.MouseEvent<HTMLElement>, newSelection: string | null) => {
      try {
        if (newSelection === benchmarkQuestion.value) return;

        const runId = benchmarkQuestion.runId;

        analytics.event(
          AnalyticsEvent.WM_IA_A_BENCHMARK_SELECTION,
          {
            id: benchmarkQuestion.id,
            value: `RunId=${runId},Selection=${newSelection ?? 'unset'}`,
          },
          {
            runId,
            questionId: benchmarkQuestion.id,
            benchmarkSelection: newSelection ?? 'unset',
          }
        );

        await userEditSave({
          ...benchmarkQuestion,
          status: AnalysisQuestionStatus.ANSWERED,
          value: newSelection,
        });

        refetchQuestions();
      } catch (error) {
        enqueueSnackbar('Failed to save benchmark selection. Please try again later.', {
          variant: 'error',
        });
      }
    },
    [benchmarkQuestion, refetchQuestions, analytics]
  );

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 2,
      }}
    >
      <Typography variant="body1" color="#50658E" data-testid={tid('question')}>
        {benchmarkQuestion.prettyQuestion}
      </Typography>
      <ToggleButtonGroup
        value={benchmarkQuestion.value}
        exclusive
        onChange={handleSelectionChange}
        aria-label="view mode"
      >
        {benchmarkOptions.map((benchmarkOption) => (
          <CustomTooltip key={benchmarkOption.type} title={benchmarkOption.tooltip}>
            <StyledToggleButton
              value={benchmarkOption.type}
              selected={benchmarkQuestion.value === benchmarkOption.type}
              disabled={benchmarkOption.disabled}
              data-testid={tid('option', benchmarkOption.type)}
            >
              <Typography sx={{ color: 'inherit', fontWeight: 'bold' }}>
                {benchmarkOption.percentage}%
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Typography sx={{ color: 'inherit' }}>{benchmarkOption.value}</Typography>
                <BusinessRounded sx={{ color: 'inherit' }} />
              </Box>
            </StyledToggleButton>
          </CustomTooltip>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};

export default Benchmark;
